// v4 (V4-D3 add_files): the upload sheet the Director opens. Mounted once (App); an `upload_sheet` directive (src/ui.ts)
// opens V4-A2's UploadSheet at that level with a preset, or for a Suno song (origin "suno") that video's Suno pack,
// whose upload links the song as the video's music. The Director can't pick files: the user taps Choose files.
// While it's open the stage's bring-back guard holds (holdStage), so a Director directive doesn't pull the sheet away.
import { useEffect, useSyncExternalStore } from "react";
import { api } from "../api";
import { director } from "../director";
import { uploadPreset, uploadSingle } from "../directives";
import { parseScope } from "../library";
import { holdStage } from "../stage/actions";
import type { Project, UploadSheetDirective } from "../types";
import { useLoad } from "./bits";
import { SunoSheet } from "./SunoPack";
import { UploadSheet } from "./Upload";

let current: UploadSheetDirective | null = null;
/** 2026-11-03: files that came with the opening (a paste on the project page) */
let currentFiles: File[] | undefined;
let opens = 0;
const subs = new Set<() => void>();
const emit = () => subs.forEach((f) => f());

/** Open the upload sheet (the `upload_sheet` directive, or a tap). A second one replaces the first. */
export function openUploadSheet(d: UploadSheetDirective, files?: File[]) {
  current = { ...d };
  currentFiles = files;
  opens++;
  emit();
}
export function closeUploadSheet() {
  current = null;
  currentFiles = undefined;
  emit();
}
export const uploadSheetOpen = () => current !== null;
const subscribe = (f: () => void) => {
  subs.add(f);
  return () => void subs.delete(f);
};

export function UploadHost() {
  const req = useSyncExternalStore(subscribe, () => current, () => null);
  useEffect(() => (req ? holdStage() : undefined), [req]);
  if (!req) return null;
  if (req.origin === "suno" && req.projectId) return <SunoHost key={req.projectId} projectId={req.projectId} />;
  const preset = uploadPreset(req.kind, req.role);
  return (
    <UploadSheet
      key={`${req.scope}/${preset ?? ""}/${opens}`}
      files={currentFiles}
      scope={parseScope(req.scope)}
      scopeName={req.scopeName ?? (req.scope === "account" ? "your library" : "this level")}
      preset={preset}
      single={uploadSingle(preset)}
      onClose={closeUploadSheet}
      onDone={(items) => {
        // the item ids, so the Director can use them straight away (use_from_library)
        director.context(`Their new files: ${items.map((a) => `"${a.name}" (item id ${a.id}, ${a.kind}${a.role ? `, ${a.role}` : ""})`).join("; ")}.`);
      }}
    />
  );
}

function SunoHost({ projectId }: { projectId: string }) {
  const { data: p } = useLoad<Project | null>(() => api.project(projectId).catch(() => null), [projectId]);
  if (!p) return null;
  return <SunoSheet p={p} onClose={closeUploadSheet} />;
}
